/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, FormattedNumber} from 'react-intl'
import {
    Box,
    Flex,
    Text,
    Button,
    useBreakpointValue,
    Container,
    Stack
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useUnavailableProductsInCartContext} from '../../../contexts/unavailable-products-in-cart-context'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {sendClickEvent} from '../../../utils/gtm-utils'

const StickyCheckoutBar = ({basket}) => {
    const [isVisible, setIsVisible] = useState(false)
    const navigate = useNavigation()
    const isMobile = useBreakpointValue({base: true, lg: false})
    const {UnavailableProductsInCart, unavailableProductIdsRef} =
        useUnavailableProductsInCartContext()

    useEffect(() => {
        const checkoutButton = document.querySelector('[data-testid="cart-checkout-button"]')
        const headerContainer = document.getElementById('app-header')

         if (!checkoutButton) {
            // Fallback: if button not found, hide sticky bar
            setIsVisible(false)
            return
        }

        if (isMobile) {
            const observer = new IntersectionObserver(
                ([entry]) => {
                    // Show sticky bar when real button is NOT visible
                    setIsVisible(!entry.isIntersecting)
                },
                {
                    threshold: 0.1, // Trigger when at least 10% of button is visible
                    rootMargin: '0px'
                }
            )

            observer.observe(checkoutButton)

            return () => {
                observer.disconnect()
            }
        } else {
            // Desktop: Show bar when user scrolls past the viewport height (below the fold)
            const handleScroll = () => {
                const scrollPosition = window.scrollY
                const ctaBottomPositionFromTop =
                    // Distance from top
                    checkoutButton.getBoundingClientRect().top + scrollPosition +
                    // Button height to set the max to the CTA bottom
                    checkoutButton.getBoundingClientRect().height -
                    // Header height
                    headerContainer?.getBoundingClientRect().height || 0

                setIsVisible(scrollPosition > ctaBottomPositionFromTop)
            }

            window.addEventListener('scroll', handleScroll)
            handleScroll()

            return () => window.removeEventListener('scroll', handleScroll)
        }
    }, [isMobile])

    const handleCheckout = () => {
        if (unavailableProductIdsRef.current?.length > 0) {
            UnavailableProductsInCart.onOpen()
        } else {
            sendClickEvent('checkout_now_clicked')
            navigate('/checkout')
        }
    }

    const giftCardTotal = (basket?.paymentInstruments || [])
        .filter((instrument) => instrument.paymentMethodId === 'SMARTCLIXX')
        .reduce((sum, instrument) => sum + (instrument.amount || 0), 0)

    const totalAmount = (basket?.orderTotal || basket?.productTotal || 0) - giftCardTotal
    const shouldShow = isVisible && !!basket

    return (
        <Box
            position="fixed"
            bottom={0}
            left={0}
            right={0}
            bg="white"
            borderTop="1px solid"
            borderColor="gray.200"
            boxShadow="0 -2px 10px rgba(0, 0, 0, 0.1)"
            zIndex={1000}
            transition="transform 0.3s ease-in-out"
            transform={shouldShow ? 'translateY(0)' : 'translateY(100%)'}
            visibility={shouldShow ? 'visible' : 'hidden'}
            pointerEvents={shouldShow ? 'auto' : 'none'}
            aria-hidden={!shouldShow}
        >
            <Container maxWidth="1440px" px={{base: 4, lg: '24px'}} pr={{base: 4, lg: '141px'}}>
                {isMobile ? (
                    // Mobile Layout: Stacked
                    <Stack spacing={3} pb={4} pt={3}>
                        {/* Top Row: Estimated Total and Amount */}
                        <Flex justify="space-between" align="center">
                            <Text
                                fontSize="sm"
                                fontWeight="700"
                                lineHeight="16px"
                                textTransform="uppercase"
                            >
                                <FormattedMessage
                                    defaultMessage="Estimated Total"
                                    id="order_summary.label.estimated_total"
                                />
                            </Text>
                            <Text fontSize="sm" fontWeight="700" lineHeight="16px">
                                {basket?.currency ? (
                                    <FormattedNumber
                                        style="currency"
                                        currency={basket.currency}
                                        value={totalAmount}
                                    />
                                ) : '—'}
                            </Text>
                        </Flex>
                        {/* Bottom Row: Checkout Button */}
                        <Button width="full" variant="solid" onClick={handleCheckout} size="lg">
                            <FormattedMessage
                                id="cart_cta.checkout_button"
                                defaultMessage="Checkout"
                            />
                        </Button>
                    </Stack>
                ) : (
                    // Desktop Layout: Horizontal
                    <Flex justify="space-between" align="center" gap={6} py={4}>
                        {/* Left: Estimated Total */}
                        <Flex align="baseline" gap={3}>
                            <Text
                                fontSize="sm"
                                fontWeight="700"
                                lineHeight="16px"
                                textTransform="uppercase"
                            >
                                <FormattedMessage
                                    defaultMessage="Estimated Total"
                                    id="order_summary.label.estimated_total"
                                />
                                :
                            </Text>
                            <Text fontSize="sm" fontWeight="700" lineHeight="16px">
                                {basket?.currency ? (
                                    <FormattedNumber
                                        style="currency"
                                        currency={basket.currency}
                                        value={totalAmount}
                                    />
                                ) : '—'}
                            </Text>
                        </Flex>
                        {/* Right: Checkout Button */}
                        <Button variant="solid" onClick={handleCheckout} size="lg" width="352px">
                            <FormattedMessage
                                id="cart_cta.checkout_button"
                                defaultMessage="Checkout"
                            />
                        </Button>
                    </Flex>
                )}
            </Container>
        </Box>
    )
}

StickyCheckoutBar.propTypes = {
    basket: PropTypes.object.isRequired
}

export default StickyCheckoutBar
